import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import type { Area, Point } from "react-easy-crop";
import Cropper from "react-easy-crop";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import type { Image } from "../components/imagesPreview/ImagePreview";
import { useStorageUrlWithCredentials } from "../components/imagesPreview/storageUrlWithCredentials";
import type { CropArea } from "./mediaApi";

const ASPECT_RATIO = 16 / 9;
const MAX_ZOOM = 5;
const MIN_SIZE_PERCENT = Math.round(100 / MAX_ZOOM);

interface Props {
  readonly image: Image | null;
  readonly onClose: () => void;
  /** Saves the crop, reporting a failure to the user itself before rejecting. */
  readonly onCrop: (id: string, area: CropArea) => Promise<unknown>;
}

const CropImageDialog: FC<Props> = ({ image, onClose, onCrop }) => {
  const imageUrl = useStorageUrlWithCredentials(image?.www.url);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<CropArea | null>(null);

  const handleClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
    onClose();
  };

  const handleSave = async () => {
    if (!image || !area) {
      return;
    }
    try {
      await onCrop(image.id, area);
      handleClose();
    } catch {
      // Already reported by onCrop; keep the dialog open so that the crop can be retried.
    }
  };

  return (
    <Dialog open={!!image} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Kadruj 16:9</DialogTitle>
      <DialogContent>
        <Box sx={{ position: "relative", width: "100%", height: "70vh", backgroundColor: "grey.900" }}>
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              maxZoom={MAX_ZOOM}
              aspect={ASPECT_RATIO}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(croppedArea) => setArea(toCropArea(croppedArea))}
            />
          )}
        </Box>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", marginTop: 2 }}>
          <Typography id="crop-size-label" variant="body2" sx={{ whiteSpace: "nowrap" }}>
            Rozmiar kadru
          </Typography>
          <Slider
            aria-labelledby="crop-size-label"
            min={MIN_SIZE_PERCENT}
            max={100}
            value={Math.round(100 / zoom)}
            valueLabelDisplay="auto"
            valueLabelFormat={(sizePercent) => `${sizePercent}%`}
            onChange={(_, sizePercent) => setZoom(100 / (sizePercent as number))}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Przeciągnij zdjęcie, aby ustawić kadr. Rozmiar można też zmienić kółkiem myszy.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          Anuluj
        </Button>
        <AsyncLoadingButton color="primary" disabled={!area} onClick={handleSave}>
          Zapisz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

function toCropArea(croppedAreaInPercent: Area): CropArea {
  const fraction = (percent: number) => Math.min(Math.max(percent / 100, 0), 1);
  return {
    leftPercentage: fraction(croppedAreaInPercent.x),
    topPercentage: fraction(croppedAreaInPercent.y),
    rightPercentage: fraction(croppedAreaInPercent.x + croppedAreaInPercent.width),
    bottomPercentage: fraction(croppedAreaInPercent.y + croppedAreaInPercent.height),
  };
}

export default CropImageDialog;
